import dayjs, { Dayjs } from 'dayjs';

/**
 * BOXY-409 — the orders export: what to send, how to deliver it.
 *
 * Pure helpers so the export modal's rules are testable without rendering it.
 */

/**
 * Exports up to this many orders download at once; larger ones go to the async
 * queue. Matches core `order.export.sync_max_rows`, which refuses the direct
 * download above it with EXPORT_TOO_LARGE_CODE.
 */
export const EXPORT_SYNC_MAX_ORDERS = 2000;

/** Core's refusal of a direct download that grew past EXPORT_SYNC_MAX_ORDERS. */
export const EXPORT_TOO_LARGE_CODE = 'EXPORT_TOO_LARGE_FOR_DIRECT_DOWNLOAD';

/** True for core's EXPORT_TOO_LARGE_CODE answer (the parsed JSON body of the 422). */
export const isExportTooLarge = (body: unknown): boolean =>
  (body as { object?: { error_code?: string } } | null)?.object?.error_code ===
  EXPORT_TOO_LARGE_CODE;

/**
 * The longest Created window an export may span (owner 10-06: six months).
 * Core refuses more than `order.export.max_days` (184) days between from and
 * to, which any 6 calendar months fit.
 */
export const EXPORT_MAX_RANGE_MONTHS = 6;

/** A count in the UI language's digits (Arabic-Indic under ar — bare `ar` is Latin in Chrome). */
export const formatExportCount = (count: number, language: string) =>
  count.toLocaleString(language.startsWith('ar') ? 'ar-IQ' : 'en-US');

export const EXPORT_DAY_FORMAT = 'YYYY-MM-DD';

/** "1 Sep – 30 Sep 2026" in the UI language (the year once when shared). */
export const formatExportRange = (from: Dayjs, to: Dayjs, language: string) => {
  const locale = language.startsWith('ar') ? 'ar-IQ' : 'en-GB';
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const full = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const start = (from.year() === to.year() ? dayMonth : full).format(from.toDate());
  return `${start} – ${full.format(to.toDate())}`;
};

export type ExportScope = 'selected' | 'tab' | 'all' | 'custom';

// Owner 10-06: week, month, 3 months, 6 months (the cap), or a custom range.
export type ExportDatePreset =
  | 'last_7_days'
  | 'last_30_days'
  | 'last_3_months'
  | 'last_6_months'
  | 'custom';

export const EXPORT_DATE_PRESETS: ExportDatePreset[] = [
  'last_7_days',
  'last_30_days',
  'last_3_months',
  'last_6_months',
  'custom',
];

/** The rows ticked on the orders list. `count` is N when select-all is on. */
export interface ExportSelection {
  uids: string[];
  isAllSelected: boolean;
  count: number;
}

export interface ExportQueryInput {
  scope: ExportScope;
  selection: ExportSelection;
  /** The list on screen as a query (`filter_list` + its filters) — useCurrentListExport. */
  tabQuery: string;
  /** Order status slugs for a custom export. */
  statuses: string[];
  /** The Period — sent with every scope but ticked uids. */
  from?: Dayjs;
  to?: Dayjs;
}

export const buildExportQuery = ({
  scope,
  selection,
  tabQuery,
  statuses,
  from,
  to,
}: ExportQueryInput): string => {
  const params = new URLSearchParams();
  // The period wins over any date the list on screen carries.
  const withPeriod = (base: URLSearchParams | string) => {
    const query = new URLSearchParams(base);
    if (from) query.set('filter_created_at_from', from.format(EXPORT_DAY_FORMAT));
    if (to) query.set('filter_created_at_to', to.format(EXPORT_DAY_FORMAT));
    return query.toString();
  };

  switch (scope) {
    case 'selected':
      // Select-all means every order of the list, not the page of ticked uids —
      // so it is "This tab" with the period (core would default it to 30 days).
      if (selection.isAllSelected) return withPeriod(tabQuery);
      selection.uids.forEach((uid) => params.append('filter_uids[]', uid));
      return params.toString();
    case 'tab':
      return withPeriod(tabQuery);
    case 'all':
      return withPeriod('');
    case 'custom':
      statuses.forEach((status) => params.append('filter_statuses[]', status));
      return withPeriod(params);
  }
};

/** A preset's [from, to] in the browser's local days; `custom` has none. */
export const presetRange = (
  preset: ExportDatePreset,
  today: Dayjs = dayjs(),
): [Dayjs, Dayjs] | undefined => {
  const day = today.startOf('day');
  switch (preset) {
    case 'last_7_days':
      return [day.subtract(6, 'day'), day];
    case 'last_30_days':
      return [day.subtract(29, 'day'), day];
    case 'last_3_months':
      return [day.subtract(3, 'month'), day];
    case 'last_6_months':
      return [day.subtract(EXPORT_MAX_RANGE_MONTHS, 'month'), day];
    case 'custom':
      return undefined;
  }
};

/** The i18n key of what is wrong with a Created window, or null when it is valid. */
export const validateExportRange = (
  from: Dayjs | undefined,
  to: Dayjs | undefined,
  today: Dayjs = dayjs(),
): string | null => {
  if (!from || !to) return 'export_modal.error_date_required';
  if (from.isAfter(to, 'day')) return 'export_modal.error_from_after_to';
  if (to.isAfter(today, 'day')) return 'export_modal.error_future_date';
  if (from.isBefore(to.subtract(EXPORT_MAX_RANGE_MONTHS, 'month'), 'day'))
    return 'export_modal.error_range_too_long';
  return null;
};

export const exportDelivery = (count: number): 'sync' | 'async' =>
  count > EXPORT_SYNC_MAX_ORDERS ? 'async' : 'sync';

export const exportScopeSlug = (scope: ExportScope, tabSlug: string): string =>
  scope === 'tab' ? tabSlug : scope;

export const exportFileName = (slug: string, today: Dayjs = dayjs()): string =>
  `boxy-orders_${slug}_${today.format(EXPORT_DAY_FORMAT)}.csv`;
